/**
 * Context-sensitive content overrides
 *
 * Convention-based: any field in a content block suffixed with `Proxy` or
 * `Minor` is automatically applied as an override when the corresponding
 * context flag is set.
 *
 * Priority (highest last): base → proxy → minor
 *
 * Example in content-mock.ts:
 *   "medrite.checkin.full-name-input": {
 *     title: t("WhatsYourName"),
 *     titleProxy: t("WhatsTheirName"),       // applied when isProxy
 *     subtitle: t("PleaseEnterYourFullName"),
 *     subtitleMinor: t("ThePatientMustPresentAValidPhotoID"), // applied when isMinor
 *   }
 *
 * Components receive the resolved content and never need to know about variants.
 */

import type { ContentBlock } from "../(render)/kiosk-provider";
import type { KioskMachineContext } from "../machine/types";

export function getContextSensitiveOverrides(
  content: ContentBlock,
  context: Partial<KioskMachineContext>,
): ContentBlock {
  const isProxy = Boolean(context.checkingInForSomeoneElse);
  const isMinor = Boolean(context.under18);

  const overrides: ContentBlock = {};

  if (isProxy) {
    for (const [key, value] of Object.entries(content)) {
      if (key.endsWith("Proxy")) {
        overrides[key.slice(0, -"Proxy".length)] = value;
      }
    }
  }

  // Minor takes precedence over proxy (but really shouldn't be used together).
  // TODO: add *Minor variant keys to content-mock.ts when minor-specific copy is finalised.
  if (isMinor) {
    for (const [key, value] of Object.entries(content)) {
      if (key.endsWith("Minor")) {
        overrides[key.slice(0, -"Minor".length)] = value;
      }
    }
  }

  return overrides;
}
